import { put, call, takeEvery, all, select } from 'redux-saga/effects'

import {
  FETCH_BRANDS,
  FETCH_VERSIONS,
  FETCH_CONTENT_TYPES,
  FETCH_BRAND_STATUS,
  FETCH_BRAND_PROGRESS,
  REINDEX_BRAND,
  STOP_REINDEX,
  REINDEX_BY_ID,
  SET_VERSIONS,
  SET_CONTENT_TYPES,
} from '../actions/types'
import {
  fetchBrandsSuccess,
  fetchBrandsFailure,
  fetchVersionsSuccess,
  fetchVersionsFailure,
  fetchContentTypesSuccess,
  fetchContentTypesFailure,
  fetchBrandStatusSuccess,
  fetchBrandStatusFailure,
  fetchBrandProgressSuccess,
  fetchBrandProgressFailure,
  reindexBrandSuccess,
  reindexBrandFailure,
  stopReindexSuccess,
  stopReindexFailure,
  reindexByIdSuccess,
  reindexByIdFailure,
  setVersionsSuccess,
  setVersionsFailure,
  fetchVersions,
  fetchBrandProgress,
  fetchBrandStatus,
} from '../actions/reindexActions'
import reindexService from '../services/reindexService'
import { fetchProgress } from '../actions/autoVersioningActions'
import { allBrandIdsSelector, brandIndexNameSelector } from '../selectors/reindexSelectors'

function* handleFetchBrands(action) {
  try {
    const { accessToken } = action.payload
    const results = yield call(reindexService.fetchBrands, accessToken)
    yield put(fetchBrandsSuccess(results))
  } catch (err) {
    console.error(err, 'Error fetching brands')
    yield put(fetchBrandsFailure(err))
  }
}

function* handleFetchVersions(action) {
  try {
    const { accessToken } = action.payload
    const results = yield call(reindexService.fetchVersions, accessToken)
    yield put(fetchVersionsSuccess(results))
  } catch (err) {
    console.error(err, 'Error fetching versions')
    yield put(fetchVersionsFailure(err))
  }
}

function* handleFetchContentTypes(action) {
  try {
    const { accessToken } = action.payload
    const results = yield call(reindexService.fetchContentTypes, accessToken)
    yield put(fetchContentTypesSuccess(results))
  } catch (err) {
    console.error(err, 'Error fetching content types')
    yield put(fetchContentTypesFailure(err))
  }
}

function* handleFetchBrandStatus(action) {
  const { brandName, indexName, contentTypes, accessToken } = action.payload

  try {
    const results = yield call(reindexService.fetchBrandStatus, accessToken, brandName, indexName, contentTypes)
    yield put(fetchBrandStatusSuccess(brandName, indexName, results))
  } catch (err) {
    console.error(err, `Error fetching brand status for '${brandName}'`)
    yield put(fetchBrandStatusFailure(brandName, indexName, err))
  }
}

function* handleFetchBrandProgress(action) {
  const { brandName, accessToken } = action.payload

  try {
    const results = yield call(reindexService.fetchBrandProgress, accessToken, brandName)
    yield put(fetchBrandProgressSuccess(brandName, results))
  } catch (err) {
    console.error(err, `Error fetching brand progress for '${brandName}'`)
    yield put(fetchBrandProgressFailure(brandName, err))
  }
}

function* handleReindexBrand(action) {
  const { brandName, contentTypes, accessToken } = action.payload

  try {
    yield call(reindexService.startReindex, accessToken, brandName, contentTypes)
    yield put(reindexBrandSuccess(brandName))
    yield put(fetchBrandProgress(brandName, accessToken))
  } catch (err) {
    console.error(err, `Error starting reindex '${brandName}'`)
    yield put(reindexBrandFailure(brandName, err))
  }
}

function* handleStopReindex(action) {
  const { brandName, accessToken } = action.payload

  try {
    yield call(reindexService.stopReindex, accessToken, brandName)
    yield put(stopReindexSuccess(brandName))
    yield put(fetchBrandProgress(brandName, accessToken))
  } catch (err) {
    console.error(err, `Error stopping reindex for '${brandName}'`)
    yield put(stopReindexFailure(brandName, err))
  }
}

function* handleReindexById(action) {
  const { documentIds, accessToken } = action.payload

  try {
    yield call(reindexService.reindexById, accessToken, documentIds)
    yield put(reindexByIdSuccess())
  } catch (err) {
    console.error(err, `Error reindexing ids [${documentIds.join(', ')}]`)
    yield put(reindexByIdFailure(err))
  }
}

function* handleSetVersions(action) {
  const { versions, accessToken } = action.payload

  try {
    yield call(reindexService.setVersions, accessToken, versions)
    yield put(setVersionsSuccess())
    yield put(fetchVersions(accessToken))
    yield put(fetchProgress(accessToken))
  } catch (err) {
    console.error(err, `Error setting versions [${versions.join(', ')}]`)
    yield put(setVersionsFailure(err))
  }
}

function* handleSetContentTypes(action) {
  const { contentTypes, accessToken } = action.payload
  const brands = yield select(allBrandIdsSelector)
  yield all(
    brands.map(function* (brand) {
      const index = yield select(brandIndexNameSelector, { id: brand })
      yield put(fetchBrandStatus(brand, index, contentTypes, accessToken))
    })
  )
}

function* watchStatusSagas() {
  yield all([
    takeEvery(FETCH_BRANDS, handleFetchBrands),
    takeEvery(FETCH_VERSIONS, handleFetchVersions),
    takeEvery(FETCH_CONTENT_TYPES, handleFetchContentTypes),
    takeEvery(FETCH_BRAND_STATUS, handleFetchBrandStatus),
    takeEvery(FETCH_BRAND_PROGRESS, handleFetchBrandProgress),
    takeEvery(REINDEX_BRAND, handleReindexBrand),
    takeEvery(STOP_REINDEX, handleStopReindex),
    takeEvery(REINDEX_BY_ID, handleReindexById),
    takeEvery(SET_VERSIONS, handleSetVersions),
    takeEvery(SET_CONTENT_TYPES, handleSetContentTypes),
  ])
}

export default watchStatusSagas
